import { BaseApiClient } from './base-client';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Visionary Studio API - Анализ стиля (Style Transfer)
 */

export interface VisionaryGenerateParams {
  mode: 'style';
  prompt: string;
  sourceImageKeys?: Array<{ s3Key: string; s3Url: string }>;
  customStyleId?: string;
  aspectRatio: string;
  count: number;
  teamId?: string | null;
}

export interface VisionaryGeneratedImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  prompt?: string;
  createdAt: string;
  s3Key?: string;
}

export interface VisionaryGenerateResponse {
  success: boolean;
  /** Present when job is queued asynchronously */
  queued?: boolean;
  /** Queued job metadata */
  data?: {
    jobId: string;
    generationId: string;
    status?: string;
    tokensDeducted?: number;
    newBalance?: number;
  };
  /** Present for synchronous (legacy) responses */
  images?: VisionaryGeneratedImage[];
  tokensUsed?: number;
  tokensRemaining?: number;
  error?: string;
}

export interface VisionaryHistoryResponse {
  images: VisionaryGeneratedImage[];
  hasMore: boolean;
  offset: number;
  limit: number;
}

export interface VisionaryCustomStyle {
  id: string;
  name: string;
  previewUrl: string;
  referenceImages: string[];
  isAnalyzing?: boolean;
  createdAt: string;
}

export interface VisionaryCreateStyleParams {
  name: string;
  imageS3Keys: Array<{ s3Key: string; s3Url: string }>;
}

export class VisionaryApi extends BaseApiClient {

  /**
   * Generate images using Анализ стиля
   */
  async generate(params: VisionaryGenerateParams): Promise<VisionaryGenerateResponse> {
    const teamId = sanitizeTeamId(params.teamId);
    const response = await this.client.post('/api/visionary/generate', { ...params, teamId });
    return response.data;
  }

  /**
   * Get generation history
   */
  async getHistory(offset = 0, limit = 20, rawTeamId?: string | null): Promise<VisionaryHistoryResponse> {
    const teamId = sanitizeTeamId(rawTeamId);
    const params: Record<string, any> = { offset, limit };
    if (teamId) params.teamId = teamId;
    const response = await this.client.get('/api/visionary/history', { params });
    return response.data;
  }

  /**
   * Delete a generated image
   */
  async deleteImage(imageId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/api/visionary/image/${imageId}`);
    return response.data;
  }

  /**
   * Get user's custom styles
   */
  async getStyles(): Promise<{ styles: VisionaryCustomStyle[] }> {
    const response = await this.client.get('/api/visionary/styles');
    return response.data;
  }

  /**
   * Create a new custom style (upload images to S3, then analyze)
   */
  async createStyle(params: VisionaryCreateStyleParams): Promise<{ style: VisionaryCustomStyle }> {
    const response = await this.client.post('/api/visionary/styles', params);
    return response.data;
  }

  /**
   * Delete a custom style
   */
  async deleteStyle(styleId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/api/visionary/styles/${styleId}`);
    return response.data;
  }

  /**
   * Rename a custom style
   */
  async renameStyle(styleId: string, name: string): Promise<{ success: boolean }> {
    const response = await this.client.patch(`/api/visionary/styles/${styleId}`, { name });
    return response.data;
  }

  /**
   * Save an uploaded reference image to DB
   */
  async saveAttachment(data: {
    s3Key: string;
    s3Url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  }): Promise<{ success: boolean; id: string }> {
    const response = await this.client.post('/api/visionary/attachments', data);
    return response.data;
  }

  /**
   * Get user's saved attachments (reference images)
   */
  async getAttachments(): Promise<{ attachments: Array<{
    id: string;
    s3Key: string;
    s3Url: string;
    fileName: string;
    createdAt: string;
  }> }> {
    const response = await this.client.get('/api/visionary/attachments');
    return response.data;
  }

  /**
   * Delete a saved attachment
   */
  async deleteAttachment(attachmentId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/api/visionary/attachments/${attachmentId}`);
    return response.data;
  }
}

export const visionaryApi = new VisionaryApi();
